/**
 * Public "thanks, you paid" page. Landed on from Stripe Checkout after a
 * customer completes payment via the one-click email link. Confirms the
 * session with the backend so the invoice is marked paid the moment they
 * hit this page (belt-and-suspenders — the Stripe webhook also finalises
 * it, but this covers webhook lag or a missed webhook).
 *
 * No login required — this is a public route.
 */
import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { company } from "@/lib/branding";

export default function PaySuccess() {
    const [state, setState] = useState({ status: "loading" });

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const sessionId = params.get("session_id");
        if (!sessionId) {
            setState({ status: "no_session" });
            return;
        }
        const base = process.env.REACT_APP_BACKEND_URL || "";
        fetch(`${base}/api/payments/status/${sessionId}`)
            .then((r) => r.json())
            .then((d) => setState({ status: "ok", data: d }))
            .catch(() => setState({ status: "error" }));
    }, []);

    return (
        <div className="min-h-screen bg-secondary/40 flex items-center justify-center p-4"
             data-testid="pay-success-page">
            <div className="bg-background rounded-xl shadow-lg p-8 max-w-md w-full text-center">
                {state.status === "loading" && (
                    <>
                        <Loader2 className="w-14 h-14 text-primary mx-auto animate-spin" />
                        <h1 className="font-heading text-2xl font-bold mt-4">Confirming payment…</h1>
                        <p className="text-sm text-muted-foreground mt-2">
                            One moment while we check with Stripe.
                        </p>
                    </>
                )}
                {state.status === "ok" && state.data?.payment_status === "paid" && (
                    <>
                        <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
                        <h1 className="font-heading text-2xl font-bold mt-4">Thanks for your payment!</h1>
                        <p className="text-sm text-muted-foreground mt-2">
                            We received {state.data.amount_total
                                ? `$${(state.data.amount_total / 100).toFixed(2)}`
                                : "your payment"}. A receipt is on its way to
                            your email.
                        </p>
                        <p className="text-xs text-muted-foreground mt-6">
                            — {company()}
                        </p>
                    </>
                )}
                {state.status === "ok" && state.data?.payment_status !== "paid" && (
                    <>
                        <Loader2 className="w-14 h-14 text-amber-500 mx-auto animate-spin" />
                        <h1 className="font-heading text-2xl font-bold mt-4">
                            Payment still processing…
                        </h1>
                        <p className="text-sm text-muted-foreground mt-2">
                            Some banks take a minute to confirm. Feel free to
                            close this window — we'll email you the moment
                            it's confirmed.
                        </p>
                    </>
                )}
                {(state.status === "error" || state.status === "no_session") && (
                    <>
                        <XCircle className="w-14 h-14 text-red-500 mx-auto" />
                        <h1 className="font-heading text-2xl font-bold mt-4">
                            Couldn't confirm your payment
                        </h1>
                        <p className="text-sm text-muted-foreground mt-2">
                            If Stripe charged your card, we'll still see it —
                            no need to pay again. Please reply to your invoice
                            email if you have any questions.
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}
